<template>
  <div
    class="modify-insurance-modal"
    data-component="ModifyInsuranceModal"
  >
    <v-form
      ref="$form"
      validate-on="input lazy"
    >
      <!-- ▼ FOR WHO ▼ -->
      <ForWhomSelectorComponent
        :forWhom="forWhom"
        :forWhomPeople="newAnswers.forWhomPeople"
        :forWhoLabel="'travel.modify-modal.for-who'"
        :maxPeople="props.configurations.maxInsurances"
        @update:forWhom="updateForWhom"
        @update:forWhomPeople="updateForWhomPeople"
      />
      <!-- ▲ FOR WHO ▲ -->

      <BaseSelect
        v-if="isModify"
        id="from"
        :label="t('copy_travel_from_form_title')"
        :items="fromOptions"
        v-model="from"
        :rules="[validationRules.required]"
      />

      <template v-if="from === 'spain'">
        <!-- ▼ INSURANCE TYPE ▼ -->
        <BaseSelect
          id="insurance-type"
          :label="t(`copy_travel_insurance-type_form-title-${forWhom}`, newAnswers.forWhomPeople)"
          :items="insuranceOptions"
          v-model="insuranceType"
          :rules="[validationRules.required]"
          @update:model-value="updatedSportInsurance"
        />
        <!-- ▲ INSURANCE TYPE ▲ -->

        <!-- ▼ SPORT INSURANCE ▼ -->
        <BaseSelect
          v-if="needsSportInsurance"
          id="sport-insurance"
          :label="t(`copy_travel_sport-insurance_form-title-${forWhom}`, newAnswers.forWhomPeople)"
          :items="yesNoOptions"
          v-model="sportInsurance"
          :rules="[validationRules.required]"
          @update:model-value="updatedSportInsurance"
        />
        <!-- ▲ SPORT INSURANCE ▲ -->

        <!-- ▼ DESTINATION TYPE ▼ -->
        <BaseSelect
          id="destination-type"
          :label="t(`copy_travel_destination-type_form-title-${forWhom}`, newAnswers.forWhomPeople)"
          :items="destinationTypesOptions"
          v-model="destinationType"
          :rules="[validationRules.required]"
        />
        <!-- ▲ DESTINATION TYPE ▲ -->

        <BaseSelect
          v-if="showCancellation"
          id="cancellation"
          :label="t('copy_travel_cancellation-coverage_form-title')"
          :items="yesNoOptions"
          v-model="cancellationCoverage"
          :rules="[validationRules.required]"
        />

        <!-- ▼ DEPARTURE DATE ▼ -->
        <DatePickerComponent
          v-if="!isSeveralTravelsType"
          id="departure-date"
          :label="
            t(`copy_travel_duration_form_departure-date-${forWhom}`, newAnswers.forWhomPeople)
          "
          :placeholder="t('copy_travel_duration_form_departure-date-placeholder')"
          v-model="departureDate"
          :datePickerVisible="!isModify"
          :min-date="minDateDeparture"
          :max-date="maxDateDeparture"
          :rules="[required]"
        />
        <!-- ▲ DEPARTURE DATE ▲ -->

        <!-- ▼ RETURN DATE ▼ -->
        <DatePickerComponent
          v-if="!isSeveralTravelsType"
          id="return-date"
          ref="returnDatePickerRef"
          :label="t(`copy_travel_duration_form_return-date-${forWhom}`, newAnswers.forWhomPeople)"
          :placeholder="t('copy_travel_duration_form_return-date-placeholder')"
          v-model="returnDate"
          :datePickerVisible="!isModify"
          :min-date="minDateReturn"
          :max-date="maxDateReturn"
          :rules="[required]"
        />
        <!-- ▲ RETURN DATE ▲ -->
      </template>

      <!-- ▼ ACTION BTNS ▼ -->
      <div class="actions">
        <BaseButton @click="submit">
          {{ t('rater.base.' + (isModify ? 'recalculate-submit' : 'submit')) }}
        </BaseButton>

        <BaseButton
          v-if="isModify"
          type="transparent"
          @click="emit('close')"
        >
          {{ t('footer.exit') }}
        </BaseButton>
        <!-- ▲ ACTION BTNS ▲ -->
      </div>
    </v-form>
  </div>
</template>

<script setup lang="ts">
  import Insured from '@/modules/common/common/models/APP/Insured'
  import AddressDetails from '@/modules/common/hiring/models/APP/AddressDetails'
  import UserDetails from '@/modules/common/hiring/models/APP/UserDetails'
  import ForWhomSelectorComponent from '@/modules/core/components/formFields/ForWhomSelectorComponent.vue'
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import BaseSelect from '@/modules/core/components/ui/BaseSelect.vue'
  import DatePickerComponent from '@/modules/core/components/ui/DatePickerComponent.vue'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { ForWhomEnum, UserRoleEnum } from '@/modules/core/models/enums'
  import type { OptionsValuesInterface } from '@/modules/core/models/types'
  import TravelConfigurations from '@/modules/travel/common/models/APP/TravelConfigurations'
  import { arrayRange } from '@/utils/array.utils'
  import { addDaysToDate, addYearsToDate } from '@/utils/date.utils'
  import { computed, onBeforeMount, ref, shallowRef, toRaw, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import TravelRaterAnswers from '../../models/APP/TravelRaterAnswers'

  const MIN_DATE = addDaysToDate(0)

  interface PropsInterface {
    answers: TravelRaterAnswers
    configurations: TravelConfigurations
    isModify?: boolean
  }
  const props = withDefaults(defineProps<PropsInterface>(), { isModify: true })

  const emit = defineEmits(['submit', 'close'])

  const { t } = useI18n()
  const { required } = useRules()

  const $form = ref()
  const newAnswers = ref(new TravelRaterAnswers(props.answers))
  const insureds = shallowRef<Insured[]>(getLoadedInsureds())
  const forWhom = ref(newAnswers.value.forWhom)
  const insuranceOptions = computed(() => props.configurations.insuranceTypesOptions)
  const insuranceTypes = computed(() => props.configurations.insuranceTypes)
  const insuranceType = ref(
    typeof props.answers.insuranceType === 'number'
      ? String(props.answers.insuranceType)
      : props.configurations.defaultInsuranceType
  )
  const destinationTypes = computed(() => props.configurations.destinationTypes)
  const destinationType = ref(
    typeof props.answers.destinationType === 'number'
      ? String(props.answers.destinationType)
      : props.configurations.defaultDestinationType
  )
  const cancellationCoverage = ref(String(props.answers.cancellationCoverage ?? false))

  const isSeveralTravelsType = computed(
    () => insuranceTypes.value.several.id === insuranceType.value
  )

  const hasSportInsurance = computed(
    () =>
      insuranceTypes.value.studyAbroad.id === String(insuranceType.value) &&
      sportInsurance.value === 'true'
  )

  const destinationTypesOptions = computed(() => {
    if (hasSportInsurance.value) {
      return props.configurations.destinationTypesOptions.filter((dto) =>
        [
          destinationTypes.value.europeAndMediterraneanCoastCountries.id,
          destinationTypes.value.restOfTheWorld.id
        ].includes(dto.value)
      )
    }
    const options = props.configurations.destinationTypesOptions.filter(
      (dto) =>
        ![destinationTypes.value.spainEuropeAndMediterraneanCoastCountries.id].includes(dto.value)
    )
    return options
  })
  const needsSportInsurance = computed(
    () => insuranceTypes.value.studyAbroad.id === insuranceType.value
  )
  const sportInsurance = ref(String(props.answers.sportInsurance ?? false))
  const minDateDeparture = computed(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
  })

  const maxDateDeparture = computed(() => addYearsToDate(props.configurations.maxYearsDeparture))

  const departureDate = ref(props.answers.departureDate ?? minDateDeparture.value)
  const returnDate = ref(props.answers.returnDate)

  const minDateReturn = computed(() => {
    const d = new Date(departureDate.value || minDateDeparture.value)
    d.setDate(d.getDate() + 1)
    return d
  })

  const maxDateReturn = computed(() =>
    addYearsToDate(props.configurations.maxYearsReturn, departureDate.value || new Date())
  )

  const returnDatePickerRef = ref<InstanceType<typeof DatePickerComponent> | null>(null)

  const yesNoOptions: OptionsValuesInterface[] = [
    { label: t('boolean-options.yes'), value: 'true' },
    { label: t('boolean-options.no'), value: 'false' }
  ]

  const fromOptions = computed<OptionsValuesInterface[]>(() => [
    { label: t(`copy_travel_from_form_spain_${forWhom.value}`), value: 'spain' },
    { label: t(`copy_travel_from_form_other_${forWhom.value}`), value: 'other' }
  ])
  const from = ref(props.answers.from ?? 'spain')

  let updatedForWhom: string | null | undefined = undefined
  const validationRules = {
    required
  }
  const showCancellation = computed(
    () =>
      insuranceType.value === insuranceTypes.value.business.id ||
      (insuranceType.value === insuranceTypes.value.studyAbroad.id && !hasSportInsurance.value)
  )

  function updateForWhom(newValue: string) {
    if (forWhom.value !== newValue) {
      updatedForWhom = forWhom.value
      forWhom.value = newValue
      newAnswers.value.forWhom = newValue
    }
  }

  function updateForWhomPeople(newValue: number) {
    if (updatedForWhom) {
      if (
        forWhom.value !== ForWhomEnum.NOT_ME &&
        (!updatedForWhom || updatedForWhom !== ForWhomEnum.NOT_ME)
      ) {
        newAnswers.value.insureds = insureds.value
        // keep holder and reset other insureds
        insureds.value = [toRaw(insureds.value[0]), ...getCleanInsureds(2, newValue, true)]
      } else {
        // reset all insureds
        insureds.value = getCleanInsureds(1, newValue, true)
      }
      updatedForWhom = undefined
    } else if (insureds.value.length !== newValue) {
      // modified people number
      if (insureds.value.length >= newValue) {
        // subtracted
        insureds.value = insureds.value.slice(0, newValue).map((i) => toRaw(i))
      } else {
        // added
        insureds.value = [
          ...insureds.value,
          ...getCleanInsureds(insureds.value.length + 1, newValue)
        ].map((i) => toRaw(i))
      }
    }

    if (newAnswers.value.forWhom === ForWhomEnum.NOT_ME) {
      // holder removed
      insureds.value[0].role = UserRoleEnum.INSURED
    } else {
      // holder added
      insureds.value[0].role = UserRoleEnum.HOLDER_INSURED
    }
    newAnswers.value.insureds = insureds.value
    newAnswers.value.forWhomPeople = newValue
  }

  function getLoadedInsureds(): Insured[] {
    const insureds = newAnswers.value.insureds as Insured[]
    return insureds.length ? insureds : [new Insured()]
  }

  function getCleanInsureds(
    currentPosition = 1,
    length = props.answers.forWhomPeople,
    reset = false
  ) {
    return arrayRange(currentPosition, length).map((i) => {
      const isFirst = i === 1
      const isHolder = isFirst && props.answers.forWhom !== ForWhomEnum.NOT_ME
      const role = isHolder ? UserRoleEnum.HOLDER_INSURED : UserRoleEnum.INSURED
      const userDetails = new UserDetails()
      if (!reset) {
        userDetails.birthDate = insureds.value[i - 1]?.userDetails?.birthDate
      }
      return new Insured({
        userDetails,
        addressDetails: new AddressDetails({
          postalCode: insureds.value[i - 1]?.addressDetails?.postalCode
        }),
        isHolder,
        role
      })
    })
  }

  function updatedSportInsurance() {
    if (
      sportInsurance.value === 'true' &&
      ![
        props.configurations.destinationTypes.europeAndMediterraneanCoastCountries.id,
        props.configurations.destinationTypes.restOfTheWorld.id
      ].includes(destinationType.value!)
    ) {
      destinationType.value = ''
    }
  }

  async function submit() {
    const { valid } = await $form.value.validate()
    if (!valid) return
    newAnswers.value.forWhom = forWhom.value
    newAnswers.value.forWhomPeople = insureds.value.length
    newAnswers.value.insuranceType = Number(insuranceType.value)
    newAnswers.value.sportInsurance = needsSportInsurance.value
      ? sportInsurance.value === 'true'
      : undefined
    newAnswers.value.destinationType = Number(destinationType.value)
    newAnswers.value.departureDate = departureDate.value
    newAnswers.value.startDate = departureDate.value
    newAnswers.value.returnDate = returnDate.value
    newAnswers.value.from = from.value

    if (showCancellation.value) {
      newAnswers.value.cancellationCoverage = cancellationCoverage.value === 'true'
    } else {
      newAnswers.value.cancellationCoverage = undefined
    }

    if (!props.isModify) {
      newAnswers.value.quotationId = null
    }

    emit('submit', newAnswers.value)
  }

  watch(
    () => insuranceType.value,
    (newValue) => {
      sportInsurance.value === 'false'
      if (String(newValue) === String(insuranceTypes.value.several.id)) {
        departureDate.value = minDateDeparture.value
        returnDate.value = maxDateDeparture.value
      }
    }
  )

  watch(departureDate, async () => {
    if (returnDate.value && returnDatePickerRef.value) {
      await returnDatePickerRef.value.validate()
    }
  })

  onBeforeMount(() => {
    if (!forWhom.value) {
      updateForWhom(ForWhomEnum.ME)
      updateForWhomPeople(1)
    }
  })
</script>

<style lang="scss" scoped>
  .modify-insurance-modal {
    max-width: $size-350;

    .tariff-subtitle {
      color: var(--pricing-subtitle-color);
      font-size: $font-size-l;
      text-transform: uppercase;
      margin-bottom: $spacing-xs;

      @include breakpoint-md {
        text-align: left;
      }
    }

    label {
      color: $color-neutral-dark;
    }

    .form-legend {
      font-size: $font-size-2xl;
      font-weight: $font-weight-500;
      line-height: $line-height-42;
      margin-bottom: $spacing-xl;
      text-align: left;
      text-wrap: balance;
    }

    .relationship-type-legend {
      padding-bottom: $spacing-xl;
      display: block;
    }

    .actions {
      margin-top: $spacing-2;
      padding-bottom: $spacing-2xl;
    }

    .start-date_form-field {
      margin-top: $spacing-2;
    }

    .add-guarantees-legend {
      margin-bottom: $spacing-m;
      color: $color-neutral-dark;
    }

    .AdvantageSliderComponent {
      max-width: $size-277 !important;
      margin: 0 auto;
    }

    .last-hr {
      margin-top: unset;
    }

    .extra-mb {
      margin-bottom: $spacing-xl;
    }
  }
</style>
